import {
  assertEnv,
  getAffiliateRefIdFromSearchString,
} from "@homeinstead/shared";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { getPageCategoryFromPath } from "./utils/get-page-category-from-path";
import { getUserAgentDataFallback } from "./utils/get-user-agent-data-fallback";

const AFFILIATE_REF_ID_TRAIT_KEY = "affiliate_ref_id";

let analytics: AnalyticsBrowser | undefined;

/**
 * Initialize Analytics - should only be called by analytics.ts
 * @returns AnalyticsBrowser instance or undefined if not available
 */
function initializeAnalytics(): AnalyticsBrowser | undefined {
  // Skip initialization on server for now
  if (typeof window === "undefined") {
    // AnalyticsBrowser is not available on server
    return undefined;
  }

  if (analytics) {
    return analytics;
  }

  let writeKey;

  try {
    writeKey = assertEnv(
      "NEXT_PUBLIC_SEGMENT_WRITE_KEY",
      process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
    );
  } catch {
    return undefined;
  }

  analytics = new AnalyticsBrowser();

  // Integrate with Consent Management Platform (CMP) if available.
  // We use Osano CMP which provides the `withCMP` function (https://cmp.osano.com/osano.segment.js). To load the Osano
  // scripts, use the `DataPrivacyManagementScripts` component from `@homeinstead/analytics/components`.
  // @see https://developers.osano.com/cmp/javascript-api/developer-documentation-segment
  if (typeof window.withCMP !== "undefined") {
    window.withCMP(analytics);
  }

  analytics.load({ writeKey });

  analytics.addSourceMiddleware(({ payload, next }) => {
    const { type } = payload.obj;

    const affiliateRefId = getAffiliateRefIdFromSearchString(
      window.location.search,
    );

    if (type && ["track", "page"].includes(type)) {
      payload.obj.context = payload.obj.context || {};

      payload.obj.context.website_platform = "nextjs";

      if (affiliateRefId) {
        payload.obj.context.traits = {
          ...(payload.obj.context.traits || {}),
          [AFFILIATE_REF_ID_TRAIT_KEY]: affiliateRefId,
        };
      }

      const pageCategory = getPageCategoryFromPath(window.location.pathname);
      if (pageCategory) {
        payload.obj.properties = {
          ...(payload.obj.properties || {}),
          page_category: pageCategory,
        };
      }

      // Add userAgentData fallback for browsers without native Client Hints API
      // @note Segment handles this automatically for Chromium browsers.
      // @see
      //  - https://developer.mozilla.org/en-US/docs/Web/API/User-Agent_Client_Hints_API#browser_compatibility
      //  - https://www.twilio.com/docs/segment/connections/spec/common#context
      if (!navigator.userAgentData) {
        payload.obj.context.userAgentData = getUserAgentDataFallback();
      }
    }

    if (type === "identify") {
      if (affiliateRefId) {
        payload.obj.traits = {
          ...(payload.obj.traits || {}),
          [AFFILIATE_REF_ID_TRAIT_KEY]: affiliateRefId,
        };
      }
    }

    next(payload);
  });

  return analytics;
}

const instance = initializeAnalytics();

/**
 * Get analytics instance or attempt to reinitialize if not available
 *
 * If unavailable, a console warning will be logged for {logEvent}
 * @param logEvent Optional event name for logging
 */
function getAnalytics({
  logEvent,
}: {
  logEvent?: "page" | "track" | "identify";
}) {
  if (typeof window === "undefined") {
    return; // nothing happening on the server
  }

  const analytics = instance || initializeAnalytics();

  if (analytics) {
    return analytics;
  }

  console.warn(
    `Analytics unavailable${logEvent ? ` for ${logEvent} event` : ""}`,
  );
}

export { getAnalytics };
